import { useCallback } from 'react';
import Pica from 'pica';
import { toastService } from '../services/ToastService';
import { isLargeFile, shouldBePreCompressed, shouldBeResized, urlAndS3Encode } from '../shared/fileUtils';
import FilesAPI from '../api/FilesAPI';
import { simplifyUrl } from '../shared/urlUtils';
import { useTranslation } from 'react-i18next';
import CONFIG from '../config/config';

type TransformerType = (resp: any) => any;

const pica = Pica();

export async function resizeImageReliable(
  file: File,
  maxWidth = 1500,
  maxHeight = 1500,
  quality = 0.9
): Promise<File> {
  const img = new Image();
  const url = URL.createObjectURL(file);
  try {
    img.src = url;
    if (img.decode) await img.decode();
    else await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error("Image load failed"));
    });

    const w = (img as any).naturalWidth ?? img.width;
    const h = (img as any).naturalHeight ?? img.height;

    const ratio = Math.min(maxWidth / w, maxHeight / h, 1);
    const newW = Math.max(1, Math.floor(w * ratio));
    const newH = Math.max(1, Math.floor(h * ratio));

    const canvas = document.createElement("canvas");
    canvas.width = newW;
    canvas.height = newH;

    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, newW, newH);

    const blob = await pica.toBlob(canvas, file.type || "image/jpeg", quality);
    return new File([blob], file.name, { type: blob.type || file.type });
  } finally {
    URL.revokeObjectURL(url);
  }
}

const compressImage = async (file: File) => {
    let quality = 0.55;
    let resultFile = file;

    while (shouldBePreCompressed(resultFile) && quality > 0.05) {
        console.log(`New size: ${resultFile.size}`);
        
        const img = new Image();
        const objectUrl = URL.createObjectURL(resultFile);
        img.src = objectUrl;
        await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
        });

        const targetWidth = Math.floor(img.width * 0.75);
        const targetHeight = Math.floor(img.height * 0.75);
        
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        try {
            await pica.resize(img, canvas);
            const blob = await pica.toBlob(canvas, file.type, quality);
            resultFile = new File([blob], file.name, { type: file.type });
            
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            URL.revokeObjectURL(objectUrl);
            throw error;
        }
        
        quality -= 0.1;
    }
    return resultFile;
};

const isVideoExtension = (file: File): boolean  => {
  const fileName = file.type;

  return CONFIG.VIDEO_TYPES.some(ext => fileName.endsWith(ext));
}

export function useImageUploader(
  apiEndpoint: string = 'files/collections/uploadV2',
  transformer: TransformerType = resp => resp.data.url,
  domain?: string
  ) {

  const { t } = useTranslation();

  const uploadImage = useCallback(async (file: File, endpointPath?: string, optBody: any = {}) => {
    const fileWithS3Encoding = urlAndS3Encode(file.name);
    const displayFileName = decodeURI(fileWithS3Encoding);
    try {
      if (isVideoExtension(file)) {
        const uploadUrl = (await FilesAPI.getUploadUrl(fileWithS3Encoding, domain)).data.url;
        const response = await FilesAPI.uploadHeavyFile(uploadUrl, file, fileWithS3Encoding);
        if (response.status === 200) {
          return simplifyUrl(uploadUrl);
        } else {
          throw new Error();
        }
      }

      if (domain === "catalogues" && (await shouldBeResized(file))) {
        file = await resizeImageReliable(file);
      }
      if (domain === "catalogues" && shouldBePreCompressed(file)) {
        file = await compressImage(file);
      }

      if (isLargeFile(file)) {
        const uploadUrl = (await FilesAPI.getUploadUrl(fileWithS3Encoding, domain)).data.url;
        const response = await FilesAPI.uploadHeavyFile(uploadUrl, file, fileWithS3Encoding);
        if (response.status === 200) {
          return simplifyUrl(uploadUrl);
        } else {
          throw new Error();
        }
      } else {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('requestBody', new Blob([JSON.stringify(optBody)], { type: 'application/json' }));
        const response = await FilesAPI.upload(`/v1/${endpointPath ? endpointPath : apiEndpoint}`, fileWithS3Encoding, formData);
        toastService.showToast(`${t("toast.success.add_file")}: ${displayFileName}.`);
        return transformer(response)
      }
    } catch (error) {
      toastService.showToast(`${t("toast.errors.add_file")}: ${displayFileName}.`, 'error');
      throw error;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiEndpoint, transformer, domain]);

  return { uploadImage };
}
